import React, { useEffect, useMemo, useState } from 'react'
import axios from 'axios'
import DataTable from 'react-data-table-component'
import moment from 'moment'
import ReactPlayer from 'react-player'

import Panel from '../ui/Panel'
import Btn from '../ui/Btn'
import Avatar from '../ui/Avatar'
import { posterUrl, videoUrl } from '../ui/videoSource'
import {
  Confirm,
  Empty,
  IconBan,
  IconOpen,
  IconTrash,
  LineSkeleton,
  Modal,
  Note,
  Pill,
  Search,
  TableSkeleton,
  firstOf,
  fullName,
  haystack,
  useTableStyles
} from './peopleUI'
import './people.css'

/*
 * Content Reports — the moderation queue.
 *
 * Every axios call is byte-for-byte the one that was here before:
 *   GET  admin/contentreports
 *   POST admin/contentreports/getcontent   (the whole report row)
 *   GET  admin/contentreports/deactivateusr/:uuid
 *   POST admin/contentreports/deleteusr    (the whole report row)
 *   POST admin/contentreports/deletepost   (the whole report row)
 *
 * What a moderator actually needs, and now gets:
 *
 *   WHO — the reported person is the subject of the row, with their face, and
 *   the reporter sits beside them. Both parties are named again inside the
 *   dialog so a decision is never taken against a half-remembered name.
 *
 *   WHAT — the reported post is fetched and shown in full: its text, its photo,
 *   its clip, and when it was created.
 *
 *   THE DECISION — three outcomes, each spelled out in a sentence before it can
 *   be taken. window.confirm() is gone: confirming happens inside the dialog,
 *   the destructive button carries the critical colour, and it names the person
 *   or the post it is about to remove.
 *
 * Three defects around those decisions are fixed on the way:
 *
 *   - the review control only rendered on reports still marked open, so a
 *     handled report offered no way to act on the account behind it;
 *   - deactivate was passed content.UserUUID alone, which is undefined
 *     whenever getcontent has not returned or has failed, sending
 *     deactivateusr/undefined; the row's own uuid now stands behind it and
 *     the control says so when neither is available;
 *   - deletepost announced success no matter what came back.
 *
 * One rendering bug is fixed on the way: the table used to be fed
 * `data.filter(x => x.SportID == item.SportID)`, where `item` is the report
 * currently open in the modal — so before anything was opened the queue was
 * filtered against `undefined`. The queue now shows the queue, and the status
 * filter below is the deliberate one.
 */

const TABS = [
  { key: 'open', label: 'Open' },
  { key: 'handled', label: 'Handled' },
  { key: 'all', label: 'All' }
]

const isOpen = (row) => row.IsActive == 1

export default function ContentReports() {
  const [item, setItem] = useState({})
  const [isLoading, setIsLoading] = useState(true)
  const [data, setData] = useState([])
  const [show, setShow] = useState(false)
  const [content, setContent] = useState({})

  const [contentLoading, setContentLoading] = useState(false)
  const [contentFailed, setContentFailed] = useState(false)
  const [tab, setTab] = useState('open')
  const [q, setQ] = useState('')
  const [note, setNote] = useState(null)
  const [failed, setFailed] = useState(false)
  const [pending, setPending] = useState(null)   // 'deactivate' | 'deleteUser' | 'deletePost'
  const [busy, setBusy] = useState(false)

  const tableStyles = useTableStyles(false)

  const reportedName = (row) => fullName(row.ReportedUsrFirstName, row.ReportedUsrLastName)
  const reportingName = (row) => fullName(row.ReportingUsrFirstName, row.ReportingUsrLastName)

  /* The API spells a party's uuid differently across endpoints; take whichever
     one this row happens to carry. Missing is fine — Avatar falls back to
     initials rather than requesting a face that does not exist. */
  const reportedUuid = (row) =>
    firstOf(row.ReportedUsrUUID, row.ReportedUserUUID, row.ReportedUUID) || undefined
  const reportingUuid = (row) =>
    firstOf(row.ReportingUsrUUID, row.ReportingUserUUID, row.ReportingUUID) || undefined

  const columns = useMemo(() => [
    {
      name: 'Reported user',
      selector: (row) => reportedName(row),
      cell: (row) => (
        <div className="pp-face">
          <Avatar uuid={reportedUuid(row)} name={reportedName(row)} size={32} />
          <div className="pp-cell">
            <span className="pp-cell-line">{reportedName(row) || '—'}</span>
            <span className="pp-cell-sub">reported by {reportingName(row) || 'unknown'}</span>
          </div>
        </div>
      ),
      sortable: true,
      minWidth: '230px',
      grow: 2
    },
    {
      name: 'Reported by',
      selector: (row) => reportingName(row),
      cell: (row) => (
        <div className="pp-face">
          <Avatar uuid={reportingUuid(row)} name={reportingName(row)} size={26} />
          <span className="pp-cell-line">{reportingName(row) || '—'}</span>
        </div>
      ),
      sortable: true,
      minWidth: '190px',
      hide: 'md'
    },
    {
      name: 'Report date',
      selector: (row) => row.ReportDate || '',
      cell: (row) => (
        <span className="pp-mono-dim">
          {row.ReportDate ? moment(row.ReportDate).format('DD/MM/YYYY') : '—'}
        </span>
      ),
      sortable: true,
      width: '128px'
    },
    {
      name: 'Notes',
      selector: (row) => row.ReportDetail || '',
      cell: (row) => (
        row.ReportDetail
          ? <span className="pp-wrap">{row.ReportDetail}</span>
          : <span className="pp-dash">—</span>
      ),
      sortable: true,
      minWidth: '230px',
      grow: 3,
      wrap: true
    },
    {
      name: 'Status',
      selector: (row) => (isOpen(row) ? 0 : 1),
      cell: (row) => (
        isOpen(row)
          ? <Pill tone="accent">Open</Pill>
          : <Pill tone="flat">Handled</Pill>
      ),
      sortable: true,
      width: '116px'
    },
    {
      name: '',
      /* Every report opens, handled ones included. A report does not stop
         being actionable because somebody looked at it once: the account
         behind a handled report can still need deactivating or deleting,
         and hiding the only way in is what made the client ask whether
         delete existed at all. */
      cell: (row) => (
        <button
          type="button"
          className="pp-rowbtn"
          title={isOpen(row) ? 'Review this report' : 'Open this report again'}
          aria-label={
            (isOpen(row) ? 'Review the report against ' : 'Open the handled report against ') +
            (reportedName(row) || 'this user')
          }
          onClick={() => handleShow(row)}
        >
          <i><IconOpen /></i>
        </button>
      ),
      width: '60px',
      center: true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [])

  useEffect(() => {
    loadData()
  }, [])

  const loadData = () => {
    setIsLoading(true)
    setFailed(false)
    axios.get(process.env.REACT_APP_API_SERVER + 'admin/contentreports', {
      headers: {
        'Authorization': 'Bearer ' + sessionStorage.getItem("apiToken")
      }
    })
      .then(res => {
        setData(res.data)
        setIsLoading(false)
      }).catch(err => {
        setIsLoading(false)
        setFailed(true)
        console.log(err)
      })
  }

  const handleClose = () => {
    setShow(false)
    setPending(null)
  }

  const handleShow = (row) => {
    setItem(row)
    setContent({})
    setPending(null)
    setContentFailed(false)
    loadContent(row)
    setShow(true)
  }

  /* The report row itself is the payload — unchanged. Note that this used to
     drive the page-level spinner, which blanked the queue behind the dialog;
     it now has its own flag. */
  const loadContent = (row) => {
    setContentLoading(true)
    axios.post(process.env.REACT_APP_API_SERVER + 'admin/contentreports/getcontent', row, {
      headers: {
        'Authorization': 'Bearer ' + sessionStorage.getItem("apiToken")
      }
    })
      .then(res => {
        if (res.data.err == 0) {
          setContent(res.data)
        } else {
          setContentFailed(true)
        }
        setContentLoading(false)
      }).catch(err => {
        setContentLoading(false)
        setContentFailed(true)
        console.log(err)
      })
  }

  const deactivateUser = (uuid) => {
    setBusy(true)
    axios.get(process.env.REACT_APP_API_SERVER + 'admin/contentreports/deactivateusr/' + uuid, {
      headers: {
        'Authorization': 'Bearer ' + sessionStorage.getItem("apiToken")
      }
    })
      .then(res => {
        setBusy(false)
        if (res.data.err == 0) {
          setShow(false)
          setPending(null)
          /* the queue reflects the decision straight away */
          loadData()
          setNote({ tone: 'good', text: (reportedName(item) || 'The user') + ' has been deactivated.' })
        } else {
          setNote({ tone: 'crit', text: 'An error has occured' })
        }
      }).catch(err => {
        setBusy(false)
        setNote({ tone: 'crit', text: 'An error has occured' })
        console.log(err)
      })
  }

  const deleteUser = () => {
    setBusy(true)
    axios.post(process.env.REACT_APP_API_SERVER + 'admin/contentreports/deleteusr', item, {
      headers: {
        'Authorization': 'Bearer ' + sessionStorage.getItem("apiToken")
      }
    })
      .then(res => {
        setBusy(false)
        if (res.data.err == 0) {
          setShow(false)
          setPending(null)
          loadData()
          setNote({ tone: 'good', text: (reportedName(item) || 'The user') + ' has been deleted.' })
        } else {
          setNote({ tone: 'crit', text: 'An error has occured' })
        }
      }).catch(err => {
        setBusy(false)
        setNote({ tone: 'crit', text: 'An error has occured' })
        console.log(err)
      })
  }

  const deleteContent = () => {
    setBusy(true)
    axios.post(process.env.REACT_APP_API_SERVER + 'admin/contentreports/deletepost', item, {
      headers: {
        'Authorization': 'Bearer ' + sessionStorage.getItem("apiToken")
      }
    })
      .then(res => {
        setBusy(false)
        /* This used to announce success whatever came back. It now believes
           the response when the response says something went wrong, and
           still treats a body without an `err` field as the success it
           always was — so no endpoint that answers differently starts
           reporting false failures. */
        if (res.data && res.data.err && res.data.err != 0) {
          setNote({ tone: 'crit', text: 'An error has occured' })
          return
        }
        setShow(false)
        setPending(null)
        loadData()
        setNote({ tone: 'good', text: 'The reported post has been deleted.' })
      }).catch(err => {
        setBusy(false)
        setNote({ tone: 'crit', text: 'An error has occured' })
        console.log(err)
      })
  }

  const rows = Array.isArray(data) ? data : []

  const countFor = (key) =>
    key === 'all' ? rows.length : rows.filter((r) => (key === 'open' ? isOpen(r) : !isOpen(r))).length

  const shown = useMemo(() => {
    const inTab = tab === 'all'
      ? rows
      : rows.filter((r) => (tab === 'open' ? isOpen(r) : !isOpen(r)))

    const needle = q.trim().toLowerCase()
    if (!needle) return inTab

    return inTab.filter((r) => haystack([
      r.ReportedUsrFirstName, r.ReportedUsrLastName,
      r.ReportingUsrFirstName, r.ReportingUsrLastName,
      r.ReportDetail
    ]).includes(needle))
  }, [rows, tab, q])

  const openCount = countFor('open')

  const clip = content.VID ? videoUrl(content.VID) : ''
  const hasVideo = item.ContentType == 2 && content.VideoID && content.VideoID.length > 0 && clip
  const hasPhoto = item.ContentType == 2 && content.Photo && content.Photo.length > 0

  /* The uuid the deactivate endpoint needs. getcontent supplies it, but that
     call can fail on a report that is still perfectly actionable, so the row's
     own copy of the reported party's uuid stands behind it. Without a fallback
     the control fired deactivateusr/undefined and the moderator was told the
     server had erred. */
  const subjectUuid = firstOf(content.UserUUID, reportedUuid(item)) || ''

  /* What "the post" actually consists of, in words, so the confirmation can
     name the thing it is about to destroy rather than say "this post". */
  const postParts = []
  if (content.Descr) postParts.push('its text')
  if (hasPhoto) postParts.push('its photo')
  if (hasVideo) postParts.push('its clip')
  const postWhat = postParts.length === 0
    ? ''
    : postParts.length === 1
      ? postParts[0]
      : postParts.slice(0, -1).join(', ') + ' and ' + postParts[postParts.length - 1]
  const postWhen = content.DateCreated ? moment(content.DateCreated).format('DD/MM/YYYY') : ''

  return (
    <div className="pp-stack">
      {note ? (
        <Note tone={note.tone} onDismiss={() => setNote(null)}>{note.text}</Note>
      ) : null}

      {failed ? (
        <Note tone="crit" onDismiss={() => setFailed(false)}>
          The moderation queue could not be loaded. Nothing has been lost — try again.
        </Note>
      ) : null}

      <Panel
        title="Content Reports"
        subtitle={
          isLoading
            ? 'Loading…'
            : openCount > 0
              ? openCount + (openCount === 1 ? ' report waiting for a decision' : ' reports waiting for a decision')
              : 'Nothing waiting for a decision'
        }
        actions={<Btn onClick={loadData}>Refresh</Btn>}
      >
        {isLoading ? (
          <TableSkeleton rows={5} />
        ) : rows.length === 0 ? (
          <Empty title="No content has been reported">
            When a player reports someone else's post, it lands here with the post
            attached so you can decide what to do about it.
          </Empty>
        ) : (
          <>
            <div className="pp-tabs" role="tablist" aria-label="Report status">
              {TABS.map((t) => (
                <button
                  type="button"
                  role="tab"
                  key={t.key}
                  className="pp-tab"
                  aria-selected={tab === t.key}
                  onClick={() => setTab(t.key)}
                >
                  {t.label}
                  <span className="pp-tab-n">{countFor(t.key)}</span>
                </button>
              ))}
            </div>

            <div className="pp-toolbar">
              <Search
                value={q}
                onChange={setQ}
                placeholder="Search by person or note…"
                label="Search reports"
              />
              <span className="pp-toolbar-spacer" />
              <span className="pp-count"><b>{shown.length}</b> of {countFor(tab)}</span>
            </div>

            {shown.length === 0 ? (
              q.trim() ? (
                <Empty
                  title="Nothing matches"
                  action={<Btn onClick={() => setQ('')}>Clear search</Btn>}
                >
                  No report in this view mentions “{q.trim()}”.
                </Empty>
              ) : tab === 'open' ? (
                <Empty title="The queue is clear">
                  Every report has been dealt with. New ones appear here as they arrive.
                </Empty>
              ) : (
                <Empty title="Nothing here yet">
                  Reports move into this view once a decision has been taken on them.
                </Empty>
              )
            ) : (
              <div className="pp-table">
                <DataTable
                  columns={columns}
                  data={shown}
                  customStyles={tableStyles}
                  highlightOnHover
                  persistTableHead
                  fixedHeader
                  fixedHeaderScrollHeight="62vh"
                  pagination
                  paginationPerPage={25}
                  paginationRowsPerPageOptions={[25, 50, 100]}
                  noDataComponent={<Empty title="No reports" />}
                />
              </div>
            )}
          </>
        )}
      </Panel>

      {/* ---------------- REVIEW DIALOG ---------------- */}
      <Modal
        open={show}
        size="lg"
        title="Review report"
        subtitle={
          item.ReportDate
            ? 'Reported on ' + moment(item.ReportDate).format('DD/MM/YYYY')
            : undefined
        }
        onClose={handleClose}
      >
        {/* who is involved */}
        <div className="pp-parties">
          <div className="pp-party" data-role="reported">
            <Avatar
              uuid={firstOf(content.UserUUID, reportedUuid(item)) || undefined}
              name={reportedName(item)}
              size={40}
            />
            <div className="pp-party-head">
              <span className="pp-party-k">Reported</span>
              <span className="pp-party-n">{reportedName(item) || '—'}</span>
            </div>
          </div>

          <div className="pp-party">
            <Avatar uuid={reportingUuid(item)} name={reportingName(item)} size={40} />
            <div className="pp-party-head">
              <span className="pp-party-k">Reported by</span>
              <span className="pp-party-n">{reportingName(item) || '—'}</span>
            </div>
          </div>
        </div>

        {/* what the reporter said */}
        <div className="pp-field">
          <div className="pp-label">Why it was reported</div>
          <div className="pp-quote">{item.ReportDetail || ''}</div>
        </div>

        {/* the reported post itself */}
        <fieldset className="pp-fieldset">
          <legend className="pp-legend">The reported content</legend>

          {contentLoading ? (
            <LineSkeleton lines={4} />
          ) : contentFailed ? (
            <Note tone="crit">
              The reported post could not be loaded. The decisions below still apply
              to this report.
            </Note>
          ) : (
            <div className="pp-form">
              <div className="pp-meta">
                <div className="pp-meta-item">
                  <span className="pp-meta-k">Date created</span>
                  <span className="pp-meta-v">
                    {content.DateCreated ? moment(content.DateCreated).format('DD/MM/YYYY') : ''}
                  </span>
                </div>
              </div>

              {content.Descr ? <div className="pp-quote">{content.Descr}</div> : null}

              {hasVideo ? (
                <div className="pp-media">
                  <div className="pp-player">
                    <ReactPlayer
                      url={clip}
                      controls={true}
                      light={posterUrl(content.VID)}
                      width='100%'
                      height='100%'
                    />
                  </div>
                </div>
              ) : null}

              {hasPhoto ? (
                <div className="pp-media">
                  <img
                    className="pp-photo"
                    src={process.env.REACT_APP_API_SERVER + 'postfiles/' + content.ContentUUID + '/123'}
                    alt="Reported photo"
                  />
                </div>
              ) : null}

              {!content.Descr && !hasVideo && !hasPhoto ? (
                <div className="pp-hint">
                  This report carries no text, photo or clip to preview.
                </div>
              ) : null}
            </div>
          )}
        </fieldset>

        {/* ---------------- THE DECISION ----------------
            Three separate outcomes, never collapsed into one "handle it"
            button, because they destroy three different amounts of work:
            one post, nothing at all, or an entire account. Each one states
            what disappears before it can be taken, and the two that cannot
            be reversed say so in the same sentence. */}
        {pending === 'deactivate' ? (
          <Confirm
            title="Deactivate this user"
            confirmLabel="Deactivate user"
            busy={busy}
            onCancel={() => setPending(null)}
            onConfirm={() => deactivateUser(subjectUuid)}
          >
            <b>{reportedName(item) || 'This user'}</b> is switched off and disappears from
            the app. <b>Nothing is deleted:</b> their account, the reported post and the rest
            of their content all stay exactly where they are, and switching them back on
            restores the lot. This is the one decision here you can take back.
          </Confirm>
        ) : pending === 'deleteUser' ? (
          <Confirm
            title="Delete this user permanently"
            confirmLabel="Delete user permanently"
            busy={busy}
            onCancel={() => setPending(null)}
            onConfirm={deleteUser}
          >
            <b>{reportedName(item) || 'This user'}</b>’s account is destroyed, and every post,
            photo and clip on it goes with it — the reported one included. Their name stops
            appearing anywhere in the app. <b>This cannot be undone</b>, and re-creating the
            account will not bring any of it back.
          </Confirm>
        ) : pending === 'deletePost' ? (
          <Confirm
            title="Delete this post permanently"
            confirmLabel="Delete post permanently"
            busy={busy}
            onCancel={() => setPending(null)}
            onConfirm={deleteContent}
          >
            The reported post{postWhen ? <> from <b>{postWhen}</b></> : null}
            {postWhat ? <> — {postWhat} — </> : ' '}
            is destroyed. <b>This cannot be undone.</b>{' '}
            <b>{reportedName(item) || 'The user'}</b> keeps their account and everything else
            they have posted.
          </Confirm>
        ) : (
          <div className="pp-field">
            <div className="pp-label">Decision</div>
            <div className="pp-decisions">
              <button
                type="button"
                className="pp-decision"
                data-tone="crit"
                onClick={() => setPending('deletePost')}
              >
                <span className="pp-decision-t"><IconTrash /> Delete post</span>
                <span className="pp-decision-s">
                  Destroys this one post. The account and its other content stay. Permanent.
                </span>
              </button>

              <button
                type="button"
                className="pp-decision"
                data-tone="warn"
                disabled={!subjectUuid}
                onClick={() => setPending('deactivate')}
              >
                <span className="pp-decision-t"><IconBan /> Deactivate user</span>
                <span className="pp-decision-s">
                  {subjectUuid
                    ? 'Hides the account from the app. Destroys nothing, and can be switched back on.'
                    : 'Unavailable: this report does not carry the reported user’s id.'}
                </span>
              </button>

              <button
                type="button"
                className="pp-decision"
                data-tone="crit"
                onClick={() => setPending('deleteUser')}
              >
                <span className="pp-decision-t"><IconTrash /> Delete user</span>
                <span className="pp-decision-s">
                  Destroys the account and every post, photo and clip on it. Permanent.
                </span>
              </button>
            </div>

            <div className="pp-hint">
              Deactivating is reversible. The two deletions are not — each one is confirmed
              by name before anything is sent.
            </div>
          </div>
        )}
      </Modal>
    </div>
  )
}
